// src/components/PrivacyPolicy.js
import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  IconButton,
  Divider
} from '@mui/material';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import PrivacyTipRoundedIcon from '@mui/icons-material/PrivacyTipRounded';

const PrivacyPolicy = ({ open, handleClose }) => {
  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      scroll="paper"
      BackdropProps={{
        sx: {
          backgroundColor: 'var(--modal-backdrop)',
          backdropFilter: 'blur(6px)'
        }
      }}
      PaperProps={{
        sx: {
          borderRadius: 4,
          overflow: 'hidden',
          border: '1px solid var(--modal-border)',
          background: 'var(--modal-background)'
        }
      }}
    >
      <Box
        sx={{
          px: { xs: 3, md: 4 },
          py: 2.5,
          color: 'var(--modal-header-text)',
          background: 'var(--modal-header-gradient)',
          display: 'flex',
          alignItems: 'center',
          gap: 1.5
        }}
      >
        <Box
          sx={{
            width: 40,
            height: 40,
            borderRadius: 2,
            backgroundColor: 'var(--modal-header-icon-bg)',
            display: 'grid',
            placeItems: 'center'
          }}
        >
          <PrivacyTipRoundedIcon />
        </Box>
        <DialogTitle
          component="h2"
          sx={{ p: 0, m: 0, fontWeight: 800, fontSize: { xs: 20, md: 24 }, flex: 1 }}
        >
          Privacy Policy
        </DialogTitle>
        <IconButton
          aria-label="Close"
          onClick={handleClose}
          sx={{
            color: 'white',
            '&:hover': { backgroundColor: 'var(--modal-header-hover)' }
          }}
        >
          <CloseRoundedIcon />
        </IconButton>
      </Box>

      <DialogContent sx={{ px: { xs: 3, md: 4 }, py: { xs: 3, md: 4 } }}>
        <Typography variant="overline" sx={{ color: 'var(--text-muted)', letterSpacing: 1, mb: 1, display: 'block' }}>
          Amaze Puzzles™
        </Typography>
        <Typography variant="h5" sx={{ fontWeight: 800, color: 'var(--text-primary-dark)', mb: 1 }}>
          Introduction
        </Typography>
        <Typography sx={{ color: 'var(--text-secondary)', mb: 3 }}>
          Amaze Puzzles (“we”, “us”, or “our”) is committed to protecting your privacy. This Privacy Policy explains
          how we collect, use, disclose, and safeguard your information when you visit our website,
          www.amazepuzzles.com (the “Service”).
        </Typography>

        <Divider sx={{ my: 2 }} />

        <Typography variant="h6" sx={{ fontWeight: 800, color: 'var(--text-primary-dark)', mb: 1 }}>
          Information We Collect
        </Typography>
        <Typography sx={{ color: 'var(--text-secondary)', mb: 3 }}>
          We may collect personal information that you voluntarily provide to us when you sign up for our newsletter or
          fill out our contact form. This information may include your name, email address, and any other details you
          provide.
        </Typography>

        <Typography variant="h6" sx={{ fontWeight: 800, color: 'var(--text-primary-dark)', mb: 1 }}>
          Use of Information
        </Typography>
        <Typography component="div" sx={{ color: 'var(--text-secondary)', mb: 3 }}>
          We use the information we collect to:
          <Box component="ul" sx={{ pl: 3, mt: 1, mb: 0 }}>
            <li>Send you our newsletter and other marketing communications</li>
            <li>Respond to your inquiries when you use our contact form</li>
            <li>Improve and personalize our Service</li>
          </Box>
        </Typography>

        <Typography variant="h6" sx={{ fontWeight: 800, color: 'var(--text-primary-dark)', mb: 1 }}>
          Sharing of Information
        </Typography>
        <Typography sx={{ color: 'var(--text-secondary)', mb: 3 }}>
          We do not sell, trade, or otherwise transfer to outside parties your Personally Identifiable Information
          unless we provide users with advance notice. This does not include website hosting partners and other parties
          who assist us in operating our Service, so long as those parties agree to keep this information confidential.
        </Typography>

        <Typography variant="h6" sx={{ fontWeight: 800, color: 'var(--text-primary-dark)', mb: 1 }}>
          Data Security
        </Typography>
        <Typography sx={{ color: 'var(--text-secondary)', mb: 3 }}>
          We use administrative, technical, and physical security measures to help protect your personal information.
          While we have taken reasonable steps to secure the personal information you provide to us, please be aware
          that no security measures are perfect or impenetrable, and no method of data transmission can be guaranteed
          against interception or misuse.
        </Typography>

        <Typography variant="h6" sx={{ fontWeight: 800, color: 'var(--text-primary-dark)', mb: 1 }}>
          Your Rights
        </Typography>
        <Typography sx={{ color: 'var(--text-secondary)', mb: 3 }}>
          Depending on your location, you may have certain rights regarding your personal information, such as the right
          to access, correct, or delete your personal data. To exercise these rights, please contact us at
          support@amazepuzzles.com.
        </Typography>

        <Typography variant="h6" sx={{ fontWeight: 800, color: 'var(--text-primary-dark)', mb: 1 }}>
          Changes to this Privacy Policy
        </Typography>
        <Typography sx={{ color: 'var(--text-secondary)', mb: 3 }}>
          We may update this Privacy Policy from time to time. We will notify you of any changes by posting the new
          Privacy Policy on this page. You are advised to review this Privacy Policy periodically for any changes.
        </Typography>

        <Typography variant="h6" sx={{ fontWeight: 800, color: 'var(--text-primary-dark)', mb: 1 }}>
          Contact Us
        </Typography>
        <Typography sx={{ color: 'var(--text-secondary)' }}>
          If you have any questions about this Privacy Policy, please contact us at support@amazepuzzles.com.
        </Typography>
      </DialogContent>

      <DialogActions sx={{ px: { xs: 3, md: 4 }, pb: 3, pt: 0 }}>
        <Button
          onClick={handleClose}
          variant="contained"
          sx={{
            fontWeight: 800,
            borderRadius: 2,
            textTransform: 'none',
            background: 'var(--modal-header-gradient)',
            '&:hover': { opacity: 0.95 }
          }}
        >
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default PrivacyPolicy;
